import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import './pricing.css';

const SIZE_LABELS = { hot_s: 'HOT S', hot_m: 'HOT M', ice_s: 'ICE S', ice_m: 'ICE M' };
const SIZE_KEYS = ['hot_s', 'hot_m', 'ice_s', 'ice_m'];
const TIER_OPTIONS = ['tier1', 'tier2', 'tier3'];

function StorePricing({ user }) {
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [store, setStore] = useState(null);
  const [prices, setPrices] = useState([]);
  const [edited, setEdited] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [initTierOpen, setInitTierOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState('tier1');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [storeRes, priceRes] = await Promise.all([
        api.get(`/store/get/${storeId}`),
        api.get(`/menu-prices/${storeId}`),
      ]);
      setStore(storeRes.data.store || storeRes.data);
      setPrices(priceRes.data.prices || priceRes.data || []);
    } catch (e) {
      toast.error('Failed to load pricing data');
    }
    setLoading(false);
  }, [storeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePriceChange = (menuCode, sizeKey, value) => {
    const numVal = value === '' ? '' : Number(value);
    setEdited(prev => ({
      ...prev,
      [menuCode]: { ...(prev[menuCode] || {}), [sizeKey]: numVal },
    }));
  };

  const getDisplayPrice = (item, sizeKey) => {
    if (edited[item.menuCode]?.[sizeKey] !== undefined) {
      return edited[item.menuCode][sizeKey];
    }
    return item.prices?.[sizeKey] ?? '';
  };

  const hasChanges = Object.keys(edited).length > 0;

  const handleBulkSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      const updates = Object.entries(edited).map(([menuCode, changes]) => ({
        menuCode,
        prices: changes,
      }));
      await api.post(`/menu-prices/bulk-update/${storeId}`, {
        prices: updates,
        userId: user._id,
        reason: 'CMS Pricing UI bulk update',
      });
      toast.success(`${updates.length} items updated`);
      setEdited({});
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const handleInitTier = async () => {
    try {
      await api.post(`/menu-prices/init-tier/${storeId}`, { tier: selectedTier });
      toast.success(`Initialized from ${selectedTier}`);
      setInitTierOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Init failed');
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/menu-prices/${storeId}/history`);
      setHistory(res.data.history || res.data || []);
    } catch (e) {
      toast.error('Failed to load history');
    }
    setHistoryLoading(false);
  };

  const filtered = prices.filter(p =>
    !search || p.menuCode?.toLowerCase().includes(search.toLowerCase()) ||
    p.menuName?.toLowerCase().includes(search.toLowerCase())
  );

  const fmtPrice = (v) => v != null && v !== '' ? `$${(Number(v) / 100).toFixed(2)}` : '-';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) {
    return (
      <Layout title="Store Pricing" user={user}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Store Pricing" user={user}>
      <Box className="sp-root">
        {/* Header */}
        <Box className="sp-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/store')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {store?.name || 'Store'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {store?.town}, {store?.state} &middot; Tier: {store?.pricingTier || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={openHistory}>
              History
            </Button>
            <Button variant="outlined" size="small" onClick={() => setInitTierOpen(true)}>
              Init from Tier
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={!hasChanges || saving}
              onClick={handleBulkSave}
              sx={{ background: '#551FFF', '&:hover': { background: '#4318c9' } }}
            >
              {saving ? 'Saving...' : `Save (${Object.keys(edited).length})`}
            </Button>
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search menu..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: '#aaa', mr: 1 }} /> }}
            sx={{ width: 300 }}
          />
          <Chip label={`${filtered.length} items`} size="small" sx={{ ml: 1 }} />
          {hasChanges && (
            <Chip
              label={`${Object.keys(edited).length} modified`}
              color="warning"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {/* Price Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 60 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Name</TableCell>
                {SIZE_KEYS.map(k => (
                  <TableCell key={k} align="center" sx={{ fontWeight: 700, minWidth: 100 }}>
                    {SIZE_LABELS[k]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3 + SIZE_KEYS.length} align="center" sx={{ py: 4, color: '#aaa' }}>
                    {prices.length === 0 ? 'No prices set. Use "Init from Tier" to start.' : 'No matching items'}
                  </TableCell>
                </TableRow>
              ) : filtered.map((item, idx) => {
                const isEdited = !!edited[item.menuCode];
                return (
                  <TableRow key={item._id || item.menuCode} sx={{ background: isEdited ? '#fffbeb' : undefined }}>
                    <TableCell sx={{ color: '#aaa', fontSize: 12 }}>{item.displayOrder ?? idx + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontFamily: 'monospace' }}>{item.menuCode}</Typography>
                      {item.isManual && <Chip label="Manual" size="small" color="info" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.menuName}</Typography>
                    </TableCell>
                    {SIZE_KEYS.map(sizeKey => {
                      const val = getDisplayPrice(item, sizeKey);
                      const original = item.prices?.[sizeKey];
                      const changed = edited[item.menuCode]?.[sizeKey] !== undefined && edited[item.menuCode][sizeKey] !== original;
                      return (
                        <TableCell key={sizeKey} align="center">
                          <Tooltip title={original != null ? `Original: ${fmtPrice(original)}` : 'No price set'}>
                            <TextField
                              size="small"
                              type="number"
                              value={val}
                              onChange={e => handlePriceChange(item.menuCode, sizeKey, e.target.value)}
                              inputProps={{ style: { textAlign: 'center', fontSize: 13, padding: '4px 8px' } }}
                              sx={{
                                width: 90,
                                '& .MuiOutlinedInput-root': {
                                  background: changed ? '#fef3c7' : '#fff',
                                },
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Price Change History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : history.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No history records</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Menu</TableCell>
                  <TableCell align="right">Before</TableCell>
                  <TableCell align="right">After</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, i) => {
                  const prevStr = h.previousPrices
                    ? SIZE_KEYS.map(k => h.previousPrices[k] != null ? fmtPrice(h.previousPrices[k]) : '·').join(' / ')
                    : '-';
                  const newStr = h.newPrices
                    ? SIZE_KEYS.map(k => h.newPrices[k] != null ? fmtPrice(h.newPrices[k]) : '·').join(' / ')
                    : '-';
                  return (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(h.createdAt)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <span style={{ fontFamily: 'monospace' }}>{h.menuCode}</span>
                        {h.menuName && <span style={{ color: '#888', fontSize: 11 }}> {h.menuName}</span>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11 }}>{prevStr}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>{newStr}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{h.user?.username || '-'}</TableCell>
                      <TableCell><Chip label={h.changeType || 'manual'} size="small" sx={{ fontSize: 10, height: 20 }} /></TableCell>
                      <TableCell sx={{ fontSize: 11, color: '#888' }}>{h.reason || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Init Tier Dialog */}
      <Dialog open={initTierOpen} onClose={() => setInitTierOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Initialize Prices from Tier</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pt: 1 }}>
            This will set all menu prices based on the selected tier template. Existing prices will be overwritten.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Tier</InputLabel>
            <Select value={selectedTier} label="Tier" onChange={e => setSelectedTier(e.target.value)}>
              {TIER_OPTIONS.map(t => <MenuItem key={t} value={t}>{t.toUpperCase()}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInitTierOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInitTier} sx={{ background: '#551FFF' }}>Initialize</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default StorePricing;
